import React, { useState, useEffect } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import * as LucideIcons from 'lucide-react';
import ColorPicker from './ColorPicker';
import ConfiguratorAdditionalItems from './ConfiguratorAdditionalItems';
import OptionToggleCard from './OptionToggleCard';
import { useTranslation } from '../context/TranslationContext';
import { getColorByRAL } from '../data/ralColors';

const OptionsPanel = ({
  config,
  onConfigChange,
  windowTypes,
  profileSystems,
  glassTextures,
  glazingTypes,
  colors,
  openingTypes,
  hardware,
  accessories,
  dimensionConstraints,
  partsOptions,
  woodJointOptions,
  divisionTypes,
  raamtypes,
  additionalItems,
  onAdditionalItemsChange,
}) => {
  const { t } = useTranslation();
  
  // Translation mappings for option names
  const translateOptionName = (id, name) => {
    const translations = {
      'no-overhead': t('options.noOverhead'),
      'overhead': t('options.overhead'),
      'no-underlight': t('options.noUnderlight'),
      'underlight': t('options.underlight'),
      'equal': t('options.equal'),
      'divide': t('options.divide'),
      'joint-45': t('options.woodJoint45'),
      'joint-90': t('options.woodJoint90'),
      'fixed-sash': t('options.fixedSash'),
      'fixed-glass': t('options.fixedGlass'),
      'tilt-turn-left': t('options.tiltTurnLeft'),
      'tilt-turn-right': t('options.tiltTurnRight'),
      'tilt-only': t('options.tiltOnly'),
      'fixed-panel': t('options.fixedPanel'),
      'synego-1': t('options.synego'),
      'synego': t('options.synego'),
      'schuco': t('options.schuco'),
      'helder-glas': t('options.clearGlass'),
      'clear': t('options.clearGlass'),
      'chinchilla': t('options.chinchilla'),
      'satinato': t('options.satinato'),
      'double': t('options.doubleGlass24'),
      'triple': t('options.doubleGlass36'),
      'double-24': t('options.doubleGlass24'),
      'double-36': t('options.doubleGlass36')
    };
    return translations[id] || name;
  };
  
  const translateOptionDescription = (id, description) => {
    const translations = {
      'fixed-sash': t('options.fixedSashDesc'),
      'fixed-glass': t('options.fixedGlassDesc'),
      'tilt-turn-left': t('options.tiltTurnLeftDesc'),
      'tilt-turn-right': t('options.tiltTurnRightDesc'),
      'tilt-only': t('options.tiltOnlyDesc'),
      'fixed-panel': t('options.fixedPanelDesc'),
      'synego-1': t('options.synegoDesc'),
      'synego': t('options.synegoDesc'),
      'schuco': t('options.schucoDesc'),
      'helder-glas': t('options.clearGlassDesc'),
      'clear': t('options.clearGlassDesc'),
      'chinchilla': t('options.chinchillaDesc'),
      'satinato': t('options.satinatoDesc'),
      'double': t('options.doubleGlass24Desc'),
      'triple': t('options.doubleGlass36Desc'),
      'double-24': t('options.doubleGlass24Desc'),
      'double-36': t('options.doubleGlass36Desc')
    };
    return translations[id] || description;
  };
  
  // Local dimension inputs so drawing doesn't react until user finishes editing
  const [widthInput, setWidthInput] = useState(String(config.width));
  const [heightInput, setHeightInput] = useState(String(config.height));

  // Keep local inputs in sync when config is changed externally (e.g. parts auto-width)
  useEffect(() => {
    setWidthInput(String(config.width));
  }, [config.width]);

  useEffect(() => {
    setHeightInput(String(config.height));
  }, [config.height]);
  
  // Check if any parts (main, overhead or underlight) have window types with interior frames
  const hasInteriorFrames = () => {
    const interiorFrameTypes = ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'];
    
    // Check main parts
    const mainPartsHaveInteriorFrames = config.partWindowTypes?.some(type => 
      interiorFrameTypes.includes(type)
    );
    
    // Check overhead parts
    const overheadPartsHaveInteriorFrames = config.overheadPartWindowTypes?.some(type => 
      interiorFrameTypes.includes(type)
    );

    // Check underlight parts
    const underlightPartsHaveInteriorFrames = config.underlightPartWindowTypes?.some(type =>
      interiorFrameTypes.includes(type)
    );
    
    return (
      mainPartsHaveInteriorFrames ||
      overheadPartsHaveInteriorFrames ||
      underlightPartsHaveInteriorFrames
    );
  };
  
  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName.split('-').map((word, i) => 
      i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
    ).join('').replace(/^(.)/, (m) => m.toUpperCase())];
    return Icon || LucideIcons.Square;
  };
  
  // On blur / enter, enforce the 400–4000 constraints and update global config once.
  const commitDimension = (field, rawValue) => {
    const minKey = `MIN_${field.toUpperCase()}`;
    const maxKey = `MAX_${field.toUpperCase()}`;
    const min = dimensionConstraints[minKey];
    const max = dimensionConstraints[maxKey];
    const parsed = parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) {
      // Reset local input back to current config value if invalid/empty
      const fallback = String(config[field]);
      if (field === 'width') setWidthInput(fallback);
      if (field === 'height') setHeightInput(fallback);
      return;
    }
    const clamped = Math.max(min, Math.min(max, parsed));
    onConfigChange(field, clamped);
  };
  
  const availableHardware = hardware.filter(h => 
    h.applicableTo.includes(config.raamtype)
  );
  
  const availableAccessories = accessories.filter(a => 
    a.applicableTo.includes(config.raamtype)
  );

  const setAccessory = (flagField, colorField, enabled) => {
    onConfigChange(flagField, enabled);
    if (enabled && !config[colorField]) {
      onConfigChange(colorField, '7016');
    }
  };

  const partsCountPicker = (field, labelKey) => (
    <div>
      <p className="mb-2 text-xs font-medium text-gray-600">{t(labelKey)}</p>
      <div className="flex gap-1.5">
        {partsOptions.map((part) => (
          <button
            key={part.id}
            type="button"
            onClick={() => onConfigChange(field, part.value)}
            className={`flex h-9 min-w-[2.25rem] flex-1 items-center justify-center rounded-lg border text-sm font-semibold transition-all ${
              config[field] === part.value
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-400'
            }`}
          >
            {part.value}
          </button>
        ))}
      </div>
    </div>
  );

  const accessoryColorPicker = (colorField, labelKey, allowedRals) => {
    const colors = allowedRals
      .map((ral) => {
        const color = getColorByRAL(ral);
        const displayRal = color?.ral?.replace(/^0+/, '') || ral;
        return color
          ? { id: `ral-${color.ral}`, name: `RAL ${displayRal}`, hex: color.hex, ral: color.ral }
          : null;
      })
      .filter(Boolean);

    return (
      <div>
        <p className="mb-2 text-xs font-medium text-gray-600">{t(labelKey)}</p>
        <div className="flex gap-1.5">
          {colors.map((color) => (
            <button
              key={color.id}
              type="button"
              onClick={() => onConfigChange(colorField, color.ral)}
              className={`flex flex-1 flex-col items-center justify-center gap-1.5 rounded-lg border p-2 transition-all ${
                config[colorField] === color.ral
                  ? 'border-gray-900 bg-gray-50 shadow-sm'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-400'
              }`}
              title={color.name}
            >
              <div
                className="h-8 w-full rounded-md border border-gray-200"
                style={{ backgroundColor: color.hex }}
              />
              <span className="text-[10px] font-semibold text-gray-600">{color.name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const standardAccessoryColors = ['9001', '910', '7016', '9006'];

  const showHorOption = (() => {
    const mainIsTilt = ['tilt-turn-left', 'tilt-turn-right', 'tilt-only'].includes(config.raamtype);
    const anyPartIsTilt = config.partWindowTypes?.some((type) =>
      ['tilt-turn-left', 'tilt-turn-right', 'tilt-only'].includes(type)
    );
    const anyOverheadPartIsTilt = config.overheadPartWindowTypes?.some((type) =>
      ['tilt-turn-left', 'tilt-turn-right', 'tilt-only'].includes(type)
    );
    const anyUnderlightPartIsTilt = config.underlightPartWindowTypes?.some((type) =>
      ['tilt-turn-left', 'tilt-turn-right', 'tilt-only'].includes(type)
    );
    return mainIsTilt || anyPartIsTilt || anyOverheadPartIsTilt || anyUnderlightPartIsTilt;
  })();

  // Global overhead/underlight toggles are hidden when any main part uses per-part settings
  const hasPerPartOverhead =
    Array.isArray(config.overheadPerPart) && config.overheadPerPart.some(Boolean);
  const hasPerPartUnderlight =
    Array.isArray(config.underlightPerPart) && config.underlightPerPart.some(Boolean);
  const showGlobalOverheadOption = !hasPerPartOverhead;
  const showGlobalUnderlightOption = !hasPerPartUnderlight;

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden p-4 md:p-5 xl:p-6 bg-white border-r border-gray-200 touch-manipulation">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-xl xl:text-2xl font-bold text-gray-900">{t('options.configuration')}</h2>
          <p className="text-sm text-gray-500 mt-1">{t('options.configureWindowOrDoor')}</p>
        </div>
        
        {/* Parts */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.parts')}</CardTitle>
            <CardDescription className="text-xs">{t('options.numberOfWindowSections')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-2">
              {partsOptions.map(part => (
                <button
                  key={part.id}
                  onClick={() => onConfigChange('parts', part.value)}
                  className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 ${
                    config.parts === part.value
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`text-lg font-bold ${
                    config.parts === part.value ? 'text-orange-600' : 'text-gray-700'
                  }`}>{part.value}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Opties — toggle cards (overhead, underlight, dorpel, extras) */}
        <Card className="border-0 shadow-none bg-transparent">
          <CardHeader className="px-0 pb-3 pt-0">
            <CardTitle className="text-2xl font-bold text-gray-900">{t('options.optiesTitle')}</CardTitle>
            <CardDescription className="text-sm text-slate-500 mt-1">
              {t('options.optiesSubtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0 space-y-3">
            {showGlobalOverheadOption ? (
            <OptionToggleCard
              title={t('options.overhead')}
              description={t('options.overheadDesc')}
              checked={config.overhead === 'overhead'}
              onCheckedChange={(on) => onConfigChange('overhead', on ? 'overhead' : 'no-overhead')}
            >
              {partsCountPicker('overheadParts', 'options.numberOfOverheadSections')}
            </OptionToggleCard>
            ) : null}

            {showGlobalUnderlightOption ? (
            <OptionToggleCard
              title={t('options.underlight')}
              description={t('options.underlightDesc')}
              checked={config.underlight === 'underlight'}
              onCheckedChange={(on) => onConfigChange('underlight', on ? 'underlight' : 'no-underlight')}
            >
              {partsCountPicker('underlightParts', 'options.numberOfUnderlightSections')}
            </OptionToggleCard>
            ) : null}

            <OptionToggleCard
              title={t('options.rampDorpel')}
              description={t('options.rampDorpelDesc')}
              checked={(config.rampDorpel || 'no-ramp-dorpel') !== 'no-ramp-dorpel'}
              onCheckedChange={(on) =>
                onConfigChange('rampDorpel', on ? 'ramp-dorpel-30mm' : 'no-ramp-dorpel')
              }
            />

            <OptionToggleCard
              title={t('options.vensterbank')}
              description={t('options.vensterbankDesc')}
              checked={(config.vensterbank || 'no-vensterbank') !== 'no-vensterbank'}
              onCheckedChange={(on) =>
                onConfigChange('vensterbank', on ? 'vensterbank-30mm' : 'no-vensterbank')
              }
            />

            <OptionToggleCard
              title={t('quote.screenElectricallyOperated')}
              description={t('options.screenDesc')}
              checked={config.screenElectricallyOperated === true}
              onCheckedChange={(on) =>
                setAccessory('screenElectricallyOperated', 'screenColor', on)
              }
            >
              {accessoryColorPicker('screenColor', 'quote.screenColor', standardAccessoryColors)}
            </OptionToggleCard>

            <OptionToggleCard
              title={t('quote.rollerShutterElectricallyOperated')}
              description={t('options.rollerShutterDesc')}
              checked={config.rollerShutterElectricallyOperated === true}
              onCheckedChange={(on) =>
                setAccessory('rollerShutterElectricallyOperated', 'rollerShutterColor', on)
              }
            >
              {accessoryColorPicker(
                'rollerShutterColor',
                'quote.rollerShutterColor',
                standardAccessoryColors
              )}
            </OptionToggleCard>

            {showHorOption ? (
              <OptionToggleCard
                title="Hor"
                description={t('options.horDesc')}
                checked={config.hor === true}
                onCheckedChange={(on) => setAccessory('hor', 'horColor', on)}
              >
                {accessoryColorPicker('horColor', 'quote.horColor', standardAccessoryColors)}
              </OptionToggleCard>
            ) : null}
          </CardContent>
        </Card>
        
        {/* Wood Joint */}
        {woodJointOptions && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('options.woodJoint')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {woodJointOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => onConfigChange('woodJoint', option.id)}
                    className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                      config.woodJoint === option.id
                        ? 'border-orange-500 bg-orange-50 shadow-sm'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className={`text-sm font-medium ${
                      config.woodJoint === option.id ? 'text-orange-600' : 'text-gray-900'
                    }`}>{translateOptionName(option.id, option.name)}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Raamtype - Image Based (only relevant when there is a single part) */}
        {config.parts === 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.windowType')}</CardTitle>
            <CardDescription className="text-xs">{t('options.selectOpeningMechanism')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {raamtypes.map(type => {
                const Icon = getIcon(type.icon);
                return (
                  <button
                    key={type.id}
                    onClick={() => onConfigChange('raamtype', type.id)}
                    className={`p-4 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 ${
                      config.raamtype === type.id
                        ? 'border-orange-500 bg-orange-50 shadow-md'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className={`w-full h-24 mb-2 rounded flex items-center justify-center ${
                      config.raamtype === type.id ? 'bg-white' : 'bg-gray-50'
                    }`}>
                      {type.image ? (
                        <img 
                          src={type.image} 
                          alt={type.name}
                          className="max-h-full max-w-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`items-center justify-center ${type.image ? 'hidden' : 'flex'}`}
                      >
                        <Icon className={`w-8 h-8 ${
                          config.raamtype === type.id ? 'text-orange-600' : 'text-gray-500'
                        }`} />
                      </div>
                    </div>
                    <div className="text-xs font-semibold text-gray-900 mb-1">{translateOptionName(type.id, type.name)}</div>
                    <div className="text-[10px] text-gray-500">{translateOptionDescription(type.id, type.description)}</div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
        )}
        
        {/* Profile System - Image Based */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.profileSystem')}</CardTitle>
            <CardDescription className="text-xs">{t('options.selectManufacturer')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {profileSystems.map(system => (
                <button
                  key={system.id}
                  onClick={() => onConfigChange('profileSystem', system.id)}
                  className={`p-4 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 ${
                    config.profileSystem === system.id
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`w-full h-20 mb-2 rounded flex items-center justify-center p-2 ${
                    config.profileSystem === system.id ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    <img 
                      src={system.logo} 
                      alt={system.name}
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'block';
                      }}
                    />
                    <div 
                      className="hidden text-xl font-bold"
                      style={{ color: system.color }}
                    >
                      {system.name}
                    </div>
                  </div>
                  <div className="text-xs font-semibold text-gray-900 text-center">{translateOptionName(system.id, system.name)}</div>
                  <div className="text-[10px] text-gray-500 text-center mt-0.5">{translateOptionDescription(system.id, system.description)}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Dimensions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.dimensions')}</CardTitle>
            <CardDescription className="text-xs">
              {t('options.range')}: {dimensionConstraints.MIN_WIDTH}-{dimensionConstraints.MAX_WIDTH} mm
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="width" className="text-sm font-medium">{t('options.width')}</Label>
              <Input
                id="width"
                type="number"
                value={widthInput}
                onChange={(e) => setWidthInput(e.target.value)}
                onBlur={() => commitDimension('width', widthInput)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitDimension('width', widthInput);
                  }
                }}
                min={dimensionConstraints.MIN_WIDTH}
                max={dimensionConstraints.MAX_WIDTH}
                className="mt-1.5 text-lg font-semibold h-12"
              />
            </div>
            <div>
              <Label htmlFor="height" className="text-sm font-medium">{t('options.height')}</Label>
              <Input
                id="height"
                type="number"
                value={heightInput}
                onChange={(e) => setHeightInput(e.target.value)}
                onBlur={() => commitDimension('height', heightInput)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitDimension('height', heightInput);
                  }
                }}
                min={dimensionConstraints.MIN_HEIGHT}
                max={dimensionConstraints.MAX_HEIGHT}
                className="mt-1.5 text-lg font-semibold h-12"
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Glass Texture */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.glassTexture')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {glassTextures.map(texture => (
                <button
                  key={texture.id}
                  onClick={() => onConfigChange('glassTexture', texture.id)}
                  className={`w-full p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-left flex items-center space-x-3 ${
                    config.glassTexture === texture.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Thumbnail image if available */}
                  {texture.image && (
                    <div className="w-16 h-16 flex-shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                      <img
                        src={texture.image}
                        alt={texture.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          // Hide broken image but keep layout
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div>
                    <div
                      className={`text-sm font-semibold ${
                    config.glassTexture === texture.id ? 'text-orange-600' : 'text-gray-900'
                      }`}
                    >
                      {translateOptionName(texture.id, texture.name)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {translateOptionDescription(texture.id, texture.description)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Glazing Type */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.glazingType')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {glazingTypes.map(glazing => (
                <button
                  key={glazing.id}
                  onClick={() => onConfigChange('glazingType', glazing.id)}
                  className={`w-full p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-left flex items-center space-x-3 ${
                    config.glazingType === glazing.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Thumbnail image if available */}
                  {glazing.image && (
                    <div className="w-16 h-16 flex-shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                      <img
                        src={glazing.image}
                        alt={glazing.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          // Hide broken image but keep layout
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div>
                    <div
                      className={`text-sm font-semibold ${
                    config.glazingType === glazing.id ? 'text-orange-600' : 'text-gray-900'
                      }`}
                    >
                      {translateOptionName(glazing.id, glazing.name)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {translateOptionDescription(glazing.id, glazing.description)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* External Frame Color */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.exteriorColor')}</CardTitle>
            <CardDescription className="text-xs">{t('options.selectFromStandardColors')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ColorPicker
              selectedColor={config.color}
              onColorChange={(colorId) => onConfigChange('color', colorId)}
              showSearch={true}
              maxVisible={8}
            />
          </CardContent>
        </Card>
        
        {/* Interior Frame Color */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.interiorColor')}</CardTitle>
            <CardDescription className="text-xs">{t('options.selectFromStandardColors')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ColorPicker
              selectedColor={config.interiorFrameColor}
              onColorChange={(colorId) => onConfigChange('interiorFrameColor', colorId)}
              showSearch={true}
              maxVisible={8}
            />
          </CardContent>
        </Card>
        
        {/* Hardware */}
        {availableHardware.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('options.hardware')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {availableHardware.map(item => (
                  <div key={item.id} className="flex items-center space-x-3">
                    <Checkbox
                      id={item.id}
                      checked={config.hardware.includes(item.id)}
                      onCheckedChange={(checked) => {
                        const newHardware = checked
                          ? [...config.hardware, item.id]
                          : config.hardware.filter(h => h !== item.id);
                        onConfigChange('hardware', newHardware);
                      }}
                    />
                    <label htmlFor={item.id} className="text-sm font-medium cursor-pointer">
                      {item.name}
                    </label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Accessories */}
        {availableAccessories.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('options.accessory')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {availableAccessories.map(item => (
                  <div key={item.id} className="flex items-center space-x-3">
                    <Checkbox
                      id={item.id}
                      checked={config.accessories.includes(item.id)}
                      onCheckedChange={(checked) => {
                        const newAccessories = checked
                          ? [...config.accessories, item.id]
                          : config.accessories.filter(a => a !== item.id);
                        onConfigChange('accessories', newAccessories);
                      }}
                    />
                    <label htmlFor={item.id} className="text-sm font-medium cursor-pointer">
                      {item.name}
                    </label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {onAdditionalItemsChange ? (
          <ConfiguratorAdditionalItems
            items={additionalItems}
            onItemsChange={onAdditionalItemsChange}
          />
        ) : null}
      </div>
    </div>
  );
};

export default OptionsPanel;
